/**
 * The studio photo and recording consent, written once.
 *
 * WHY THE TEXT LIVES IN CODE AND IS ALSO STORED WITH THE ANSWER. Wording gets
 * edited. An answer with no record of what was agreed to is not evidence of
 * anything, so whatever is shown is saved alongside the yes or no, and a
 * later edit cannot rewrite what a family actually agreed to. Bump the
 * VERSION whenever the words change.
 *
 * WHAT WAS DELIBERATELY LEFT OUT. The Summer Institute release bundles three
 * things into one signature: media consent, a hold-harmless covering
 * employees, contractors, directors, partners and venues, and a risk
 * acknowledgement. That bundle cannot work where the consent is OPTIONAL,
 * because a parent declining photos would decline the waiver along with it.
 * Decision (Chris, 2026-10-22): studio asks about photos only. Liability is a
 * separate question for a lawyer, and is not asked here.
 *
 * WHAT WAS DELIBERATELY NOT COPIED. The SI text names "the 2026 Summer
 * Institute", so it is scoped to one event. Studio consent is ongoing, so it
 * says so plainly and says how to change it, rather than being silently
 * open-ended.
 */
export const PHOTO_RELEASE_VERSION = '2026-10-22b';

export const PHOTO_RELEASE_TEXT =
  'CFVA may photograph, film and audio record my student at lessons, classes, rehearsals and ' +
  'performances, and use it to share and promote the studio. This stands until I change it, ' +
  'which I can do any time by telling the studio. Saying no changes nothing.';

/**
 * WHY IT IS THIS SHORT. The first draft ran to 97 words and a parent skims
 * that. Every element a consent actually needs is still here, and nothing
 * else is:
 *
 *   who          CFVA
 *   what         photograph, film, audio record
 *   where        lessons, classes, rehearsals, performances
 *   what for     share and promote the studio
 *   how long     until I change it
 *   how to stop  by telling the studio
 *   the cost     none, saying no changes nothing
 *
 * The last line is load-bearing rather than polite. A consent a family
 * believes will cost them something is not freely given, and the point of
 * making this optional is that a no is a real option.
 */

/** The two answers. Declining is a real answer, not an absence of one. */
export const PHOTO_RELEASE_CHOICES = [
  { value: true, label: 'Yes, that is fine' },
  { value: false, label: 'No, please do not' },
] as const;

export type PhotoReleaseState = 'granted' | 'refused' | 'unanswered';

/**
 * NULL is not "no". It means nobody has asked, or nobody has answered, and the
 * two must never be conflated: treating silence as consent is how a child gets
 * photographed without permission, and treating it as refusal makes it
 * impossible to tell who still needs asking.
 */
export function photoReleaseState(value: boolean | null | undefined): PhotoReleaseState {
  if (value === true) return 'granted';
  if (value === false) return 'refused';
  return 'unanswered';
}

/** May the studio photograph this student right now? Only an explicit yes. */
export function mayPhotograph(value: boolean | null | undefined): boolean {
  return photoReleaseState(value) === 'granted';
}

/** Short words for a badge, where space is one line. */
export function photoReleaseLabel(value: boolean | null | undefined): string {
  switch (photoReleaseState(value)) {
    case 'granted':
      return 'Photo OK';
    case 'refused':
      return 'No photos';
    default:
      return 'Photo consent not asked';
  }
}
